import { getDefaultButtonIcon } from "@jugl-web/domain-resources/chat-bot/utils/defaultButtonIcons";
import { cx } from "@jugl-web/utils";
import { isColorDark } from "@jugl-web/utils/color";
import { FC, useEffect, useMemo, useRef } from "react";
import { ReactComponent as CloseIcon } from "./assets/close-icon.svg";
import { BUTTON_SIZE } from "../../consts";

export interface ChatBotButtonProps {
  isOpen: boolean;
  /** ID of the default icon or URL of the custom icon */
  icon: File | string;
  buttonColor: string;
  className?: string;
  onClick?: () => void;
}

export const ChatBotButton: FC<ChatBotButtonProps> = ({
  isOpen,
  icon,
  buttonColor,
  className,
  onClick,
}) => {
  const urlRef = useRef<string | null>(null);

  const customIconUrl = useMemo(() => {
    if (icon instanceof File) {
      const url = URL.createObjectURL(icon);
      urlRef.current = url;
      return url;
    }

    if (typeof icon === "string" && !icon.startsWith("default")) {
      return icon;
    }

    return null;
  }, [icon]);

  const isCustomIconSelected = !!customIconUrl;

  const isButtonDark = useMemo(() => isColorDark(buttonColor), [buttonColor]);

  const buttonClassName = cx(
    "h-[36px] w-[36px]",
    isButtonDark ? "text-white" : "text-dark"
  );

  const buttonContent = useMemo(() => {
    if (isOpen) {
      return <CloseIcon className={buttonClassName} />;
    }

    if (isCustomIconSelected) {
      return (
        <img
          src={customIconUrl}
          className="h-full w-full object-cover"
          alt="Button Icon"
        />
      );
    }

    const Icon = getDefaultButtonIcon(icon as string);

    return <Icon className={buttonClassName} />;
  }, [buttonClassName, customIconUrl, icon, isCustomIconSelected, isOpen]);

  // Cleanup the object URL when the component unmounts
  useEffect(
    () => () => {
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
      }
    },
    []
  );

  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        "flex shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-full border-none p-0 shadow-[1px_4px_13px_0px_rgba(0,0,0,0.25)] transition-transform hover:scale-105 active:scale-95",
        className
      )}
      style={{
        backgroundColor: buttonColor,
        height: BUTTON_SIZE,
        width: BUTTON_SIZE,
      }}
    >
      {buttonContent}
    </button>
  );
};
